import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/comics';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setCategoryOpen(false);
  }, [location]);

  const isHome = location.pathname === '/';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || !isHome
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500">
              <i className="ri-book-open-line text-background-50 text-lg"></i>
            </div>
            <span className={`font-heading text-xl font-bold ${scrolled || !isHome ? 'text-foreground-950' : 'text-background-50'}`}>
              韩国漫画网
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${
                location.pathname === '/'
                  ? scrolled || !isHome ? 'text-primary-500' : 'text-primary-300'
                  : scrolled || !isHome ? 'text-foreground-700 hover:text-primary-500' : 'text-background-50/80 hover:text-background-50'
              }`}
            >
              首页
            </Link>

            <div className="relative" onMouseEnter={() => setCategoryOpen(true)} onMouseLeave={() => setCategoryOpen(false)}>
              <button
                className={`flex items-center gap-1 text-sm font-medium transition-colors whitespace-nowrap ${
                  location.pathname.startsWith('/category')
                    ? scrolled || !isHome ? 'text-primary-500' : 'text-primary-300'
                    : scrolled || !isHome ? 'text-foreground-700 hover:text-primary-500' : 'text-background-50/80 hover:text-background-50'
                }`}
              >
                分类浏览
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${categoryOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {categoryOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 bg-background-50 rounded-lg border border-background-200/70 py-2 min-w-[200px] shadow-sm">
                  <div className="grid grid-cols-2 gap-1 px-2">
                    {categories.map((cat) => (
                      <Link
                        key={cat.id}
                        to={`/category/${cat.slug}`}
                        className="px-3 py-2 text-sm text-foreground-700 hover:text-primary-500 hover:bg-primary-50 rounded-md transition-colors whitespace-nowrap"
                      >
                        {cat.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/about"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${
                location.pathname === '/about'
                  ? scrolled || !isHome ? 'text-primary-500' : 'text-primary-300'
                  : scrolled || !isHome ? 'text-foreground-700 hover:text-primary-500' : 'text-background-50/80 hover:text-background-50'
              }`}
            >
              关于我们
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <div className={`relative flex items-center ${scrolled || !isHome ? 'bg-background-100 border border-background-300/60' : 'bg-background-50/20 border border-background-50/30'} rounded-full px-4 py-2 transition-colors`}>
              <i className={`ri-search-line text-sm ${scrolled || !isHome ? 'text-foreground-400' : 'text-background-50/60'} mr-2`}></i>
              <input
                type="text"
                placeholder="搜索漫画..."
                className={`bg-transparent text-sm outline-none w-32 ${scrolled || !isHome ? 'text-foreground-700 placeholder:text-foreground-300' : 'text-background-50 placeholder:text-background-50/50'}`}
              />
            </div>
          </div>

          <button
            className="md:hidden w-10 h-10 flex items-center justify-center"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="菜单"
          >
            <i className={`text-xl ${scrolled || !isHome ? 'text-foreground-950' : 'text-background-50'} ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 px-4 py-4">
          <div className="flex flex-col gap-2">
            <Link to="/" className="px-3 py-3 text-sm font-medium text-foreground-700 hover:text-primary-500 hover:bg-primary-50 rounded-lg transition-colors">
              首页
            </Link>
            <div className="px-3 py-2 text-sm font-semibold text-foreground-500">分类浏览</div>
            <div className="grid grid-cols-2 gap-1">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="px-3 py-2 text-sm text-foreground-700 hover:text-primary-500 hover:bg-primary-50 rounded-lg transition-colors"
                >
                  {cat.name}
                </Link>
              ))}
            </div>
            <Link to="/about" className="px-3 py-3 text-sm font-medium text-foreground-700 hover:text-primary-500 hover:bg-primary-50 rounded-lg transition-colors">
              关于我们
            </Link>
            <div className="relative flex items-center bg-background-100 rounded-full px-4 py-2 mt-2">
              <i className="ri-search-line text-sm text-foreground-400 mr-2"></i>
              <input type="text" placeholder="搜索漫画..." className="bg-transparent text-sm outline-none w-full text-foreground-700 placeholder:text-foreground-300" />
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}